import { hasCapability } from "@brewbase/lib";
import { domainSidebars } from "./config";
import type { DomainSidebarItem } from "./types";

/**
 * Filter a domain sidebar item list down to what the operator may see.
 *
 * - A link with a `capability` it lacks is dropped.
 * - A section is dropped if the operator lacks its own `capability`, or if it
 *   ends up with no visible (non-divider) children.
 *
 * This drives nav visibility only. Server-side `requireCapability` is the
 * actual enforcement — the underlying pages may still be reachable by URL.
 */
export function filterNavItems(
  items: DomainSidebarItem[],
  capabilities: readonly string[],
): DomainSidebarItem[] {
  const result: DomainSidebarItem[] = [];
  for (const item of items) {
    if (item.type === "divider" || item.type === "methods") {
      result.push(item);
      continue;
    }
    if (item.capability && !hasCapability(capabilities, item.capability)) {
      continue;
    }
    if (item.type === "section") {
      const children = filterNavItems(item.children, capabilities);
      if (children.some((c) => c.type !== "divider")) {
        result.push({ ...item, children });
      }
      continue;
    }
    result.push(item);
  }
  return result;
}

/** Whether the operator can see at least one item in the Settings sidebar. */
export function canSeeSettings(capabilities: readonly string[]): boolean {
  const settings = domainSidebars.settings?.items ?? [];
  return filterNavItems(settings, capabilities).some(
    (item) => item.type !== "divider",
  );
}
